import React, { forwardRef, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import clsx from 'clsx';
import { FocusOn } from 'react-focus-on';
import styles from './styles.module.css';
import { ScrollContext, SizeContext } from './context';
import { mergeRefs, shallowObjectsEqual } from '../utils';
import { useWindowSize } from '../hooks/use-window-size';
import { FocusOnContext, useFocusOnIgnore } from '../_internal/hooks';
const drawerTransition = {
  enter: styles.drawerTransitionEnter,
  enterActive: styles.drawerTransitionEnterActive,
  exit: styles.drawerTransitionExit,
  exitActive: styles.drawerTransitionExitActive
};
export function reducer(state, action) {
  switch (action.type) {
    case 'newState':
      return action.payload;
    default:
      throw new Error();
  }
}
/**
 * `<Drawer />` Элемент навигации для десктопа и мобильной версии
 *
 * @example ``` tsx
 * <Drawer
 *  onClose={() => changeShow(false)}
 *  show={true}
 * >
 *  Content
 * </Drawer>
 * ```
 */
export const Drawer = /*#__PURE__*/forwardRef(function Drawer({
  children,
  onClose,
  onInnerScroll,
  show,
  className,
  size = 'xs',
  direction = 'right',
  overlayClose = true,
  breakpoint = 440,
  ...props
}, ref) {
  const testId = props['data-testid'] || 'drawer';
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollState, scrollDispatch] = useReducer(reducer, {
    top: false,
    bottom: false
  });
  const [mobile, setMobile] = useState(false);
  const windowSize = useWindowSize();
  const {
    isTargetIgnored
  } = useFocusOnIgnore();
  useEffect(() => {
    const newState = windowSize.innerWidth <= breakpoint;
    if (mobile !== newState) {
      setMobile(newState);
    }
  }, [windowSize, mobile, breakpoint]);
  useEffect(() => {
    if (onInnerScroll) {
      onInnerScroll(scrollState == null ? void 0 : scrollState.top, scrollState == null ? void 0 : scrollState.bottom);
    }
  }, [onInnerScroll, scrollState]);
  const drawerClassName = useMemo(() => clsx(styles.drawer, styles[size], className, {
    [styles.mobile]: mobile
  }), [className, mobile, size]);
  const handlerClassName = useMemo(() => clsx(styles.handler, {
    [styles.left]: direction === 'left'
  }), [direction]);
  const checkScroll = useCallback(() => {
    if (contentRef.current) {
      const top = contentRef.current.scrollTop > 0;
      const bottom = contentRef.current.offsetHeight + contentRef.current.scrollTop < contentRef.current.scrollHeight;
      const newState = {
        top,
        bottom
      };
      if (!shallowObjectsEqual(scrollState, newState)) {
        scrollDispatch({
          type: 'newState',
          payload: newState
        });
      }
    }
  }, [scrollState]);
  const overlayCloseHandler = useCallback(event => {
    if (overlayClose && !isTargetIgnored(event.target)) {
      onClose();
    }
  }, [isTargetIgnored, onClose, overlayClose]);
  return /*#__PURE__*/React.createElement(CSSTransition, {
    nodeRef: overlayRef,
    timeout: 250,
    in: show,
    classNames: drawerTransition,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    className: drawerClassName,
    ref: overlayRef,
    "data-testid": `${testId}-overlay`
  }, /*#__PURE__*/React.createElement(FocusOn, {
    onClickOutside: overlayCloseHandler,
    onEscapeKey: onClose,
    className: handlerClassName,
    enabled: show,
    autoFocus: false
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.modal,
    role: "dialog",
    ref: mergeRefs([ref, modalRef]),
    ...props,
    "data-testid": `${testId}-modal`
  }, /*#__PURE__*/React.createElement(ScrollContext.Provider, {
    value: scrollState
  }, /*#__PURE__*/React.createElement(SizeContext.Provider, {
    value: size
  }, /*#__PURE__*/React.createElement(FocusOnContext.Provider, {
    value: true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.scrollWrapper,
    onScroll: checkScroll,
    ref: contentRef,
    "data-testid": `${testId}-content-wrapper`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.contentWrapper
  }, children)))))))));
});